// src/components/mitchRoom/revenueAnalytics/SummaryCards.js
import React, { useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Users,
  UserPlus,
  CreditCard,
  PoundSterling,
} from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { formatCurrency, formatPercent } from 'utils/format';

const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB').format(value);
};

const formatWholeCurrency = (value) => formatCurrency(value, { whole: true });

const CARDS = [
  {
    key: 'total_revenue',
    label: 'Total Revenue',
    icon: PoundSterling,
    format: formatWholeCurrency,
    color: '#59A14F',
    deltaKey: 'total_revenue',
  },
  {
    key: 'avg_per_period',
    // Label is granularity-aware — this is revenue per BUCKET, and at daily
    // granularity calling it "monthly" (and x12-ing it into an ARR) was wrong
    // by a factor of ~30.
    labelFor: (g) => `Avg per ${g === 'day' ? 'day' : g === 'week' ? 'week' : 'month'}`,
    icon: TrendingUp,
    format: formatWholeCurrency,
    color: '#1072CA',
    tooltip: 'Total revenue in range ÷ number of periods shown',
  },
  {
    key: 'total_orders',
    label: 'Orders',
    icon: CreditCard,
    format: formatNumber,
    color: '#B07AA1',
    deltaKey: 'total_orders',
  },
  {
    key: 'avg_active',
    label: 'Avg Active',
    icon: CreditCard,
    format: formatNumber,
    color: '#F28E2B',
    deltaKey: 'avg_active',
  },
  {
    key: 'customers_acquired',
    label: 'Acquired',
    icon: UserPlus,
    format: formatNumber,
    color: '#76B7B2',
    deltaKey: 'customers_acquired',
  },
  {
    key: 'total_churned',
    label: 'Total Churned',
    icon: TrendingDown,
    format: formatNumber,
    color: '#E15759',
    deltaKey: 'total_churned',
    deltaInverted: true, // churn going up is bad
  },
  {
    key: 'churn_rate',
    label: 'Churn Rate',
    icon: TrendingDown,
    format: (v) => formatPercent(v),
    color: '#E15759',
    tooltip: 'Total Churned ÷ Avg Active',
  },
  {
    key: 'net_subscribers',
    label: 'Net Change',
    icon: Users,
    format: (v) => (v > 0 ? `+${formatNumber(v)}` : formatNumber(v)),
    color: '#4E79A7',
  },
];

export default function SummaryCards({
  timeSeries,
  subscriberMetrics,
  comparison,
  loading,
  granularity = 'month',
}) {
  // Calculate metrics from the filtered time series and subscriber data
  const calculatedMetrics = useMemo(() => {
    if (!timeSeries || timeSeries.length === 0) {
      return null;
    }

    // Total revenue from time series (already net of refunds server-side)
    const totalRevenue = timeSeries.reduce((sum, point) => sum + (point.total || 0), 0);
    const totalOrders = timeSeries.reduce((sum, point) => sum + (point.orders || 0), 0);

    // Revenue per bucket at whatever granularity is showing
    const avgPerPeriod = totalRevenue / timeSeries.length;

    // Subscriber metrics from the subscriber data
    const subData = subscriberMetrics?.data || [];
    const lastPeriod = subData[subData.length - 1];
    const activeAtEnd = lastPeriod?.active || subscriberMetrics?.current_active || 0;

    // Net change in subscribers over the period
    const totalNew = subData.reduce((sum, point) => sum + (point.new || 0), 0);
    const totalChurned = subData.reduce((sum, point) => sum + (point.churned || 0), 0);
    const netSubscribers = totalNew - totalChurned;

    // Churn rate: total churned over the average active base. active_start
    // is the count BEFORE the first period's own movements — using the
    // first bucket's END count biased the denominator upward.
    const activeAtStart = subData[0]?.active_start ?? subData[0]?.active ?? activeAtEnd;
    const avgActive = (activeAtStart + activeAtEnd) / 2;
    const churnRate = avgActive > 0 ? (totalChurned / avgActive) * 100 : 0;

    return {
      total_revenue: totalRevenue,
      total_orders: totalOrders,
      avg_per_period: avgPerPeriod,
      avg_active: Math.round(avgActive),
      customers_acquired: totalNew,
      total_churned: totalChurned,
      churn_rate: churnRate,
      net_subscribers: netSubscribers,
    };
  }, [timeSeries, subscriberMetrics]);

  // Deltas: vs the selected comparison period when one is active,
  // otherwise MoM from the last two data points
  const { deltas, deltaLabel } = useMemo(() => {
    const pct = (current, previous) => {
      if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
      return ((current - previous) / Math.abs(previous)) * 100;
    };

    const result = {};

    if (comparison?.timeSeries?.length) {
      const compRevenue = comparison.timeSeries.reduce((sum, p) => sum + (p.total || 0), 0);
      const currRevenue = (timeSeries || []).reduce((sum, p) => sum + (p.total || 0), 0);
      result.total_revenue = pct(currRevenue, compRevenue);

      const compOrders = comparison.timeSeries.reduce((sum, p) => sum + (p.orders || 0), 0);
      const currOrders = (timeSeries || []).reduce((sum, p) => sum + (p.orders || 0), 0);
      result.total_orders = pct(currOrders, compOrders);

      const compSub = comparison.subscriberMetrics?.data || [];
      const currSub = subscriberMetrics?.data || [];
      const sumKey = (rows, key) => rows.reduce((sum, p) => sum + (p[key] || 0), 0);
      result.customers_acquired = pct(sumKey(currSub, 'new'), sumKey(compSub, 'new'));
      result.total_churned = pct(sumKey(currSub, 'churned'), sumKey(compSub, 'churned'));

      return {
        deltas: result,
        deltaLabel: `vs ${(comparison.label || 'comparison').toLowerCase()}`,
      };
    }

    if (timeSeries && timeSeries.length >= 2) {
      const last = timeSeries[timeSeries.length - 1];
      const prev = timeSeries[timeSeries.length - 2];
      result.total_revenue = pct(last.total || 0, prev.total || 0);
      result.total_orders = pct(last.orders || 0, prev.orders || 0);
    }
    const subData = subscriberMetrics?.data || [];
    if (subData.length >= 2) {
      const last = subData[subData.length - 1];
      const prev = subData[subData.length - 2];
      result.avg_active = pct(last.active || 0, prev.active || 0);
      result.customers_acquired = pct(last.new || 0, prev.new || 0);
      result.total_churned = pct(last.churned || 0, prev.churned || 0);
    }
    const unit = granularity === 'day' ? 'day' : granularity === 'week' ? 'week' : 'month';
    return { deltas: result, deltaLabel: `vs prev ${unit}` };
  }, [timeSeries, subscriberMetrics, comparison, granularity]);

  return (
    <div className="grid w-full grid-cols-4 gap-3 xl:grid-cols-8">
      {CARDS.map((card) => {
        const Icon = card.icon;
        const label = card.labelFor ? card.labelFor(granularity) : card.label;
        const value = calculatedMetrics?.[card.key];
        const delta = card.deltaKey ? deltas[card.deltaKey] : null;
        const deltaGood = card.deltaInverted ? delta <= 0 : delta >= 0;

        return (
          <div
            key={card.key}
            title={card.tooltip}
            className="rounded-token-large border border-borderGrey border-l-4 bg-white p-3"
            style={{ borderLeftColor: card.color }}
          >
            <div className="mb-1 flex items-center gap-1.5">
              <Icon size={14} className="shrink-0" style={{ color: card.color }} aria-hidden="true" />
              <span className="whitespace-nowrap text-token-sm text-muted-foreground">
                {label}
              </span>
            </div>
            {loading ? (
              <Skeleton className="h-6 w-16" />
            ) : (
              <>
                <p className="text-token-lg font-semibold">{card.format(value)}</p>
                {delta !== null && delta !== undefined && Number.isFinite(delta) && (
                  <p
                    className={cn(
                      'mt-0.5 text-token-xs',
                      deltaGood ? 'text-success' : 'text-destructive'
                    )}
                  >
                    {delta >= 0 ? '+' : ''}
                    {delta.toFixed(0)}% {deltaLabel}
                  </p>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
